import { useState } from "react";
import { Calendar } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/context/LanguageContext";
import { loc } from "@/lib/localize";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AppointmentScheduler from "@/components/AppointmentScheduler";
import { trackEvent } from "@/lib/analytics";

function BookingDialog({ open, onOpenChange, title, subtitle }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto bg-[var(--surface)] border border-white/10 p-0 gap-0"
        data-testid="booking-dialog"
      >
        <DialogHeader className="p-6 pb-2 text-left">
          <DialogTitle className="text-white font-display text-2xl">{title}</DialogTitle>
          <DialogDescription className="text-white/60">{subtitle}</DialogDescription>
        </DialogHeader>
        {/* Only mounted once opened — avoids fetching availability on every page visit */}
        {open && <AppointmentScheduler />}
      </DialogContent>
    </Dialog>
  );
}

// variant="hero"  — full showcase section (tag + heading + button), used on the home page.
// variant="link"  — compact inline text link, used e.g. right above the contact form.
export default function BookingSection({ variant = "hero" }) {
  const { content } = useContent();
  const { t, lang } = useLang();
  const booking = content?.booking;
  const [open, setOpen] = useState(false);

  if (!booking || !booking.enabled) return null;

  const title = loc(booking.title, lang) || t("booking.title");
  const subtitle = loc(booking.subtitle, lang) || t("booking.subtitle");

  const openDialog = () => {
    setOpen(true);
    trackEvent("consultation_cta_click", { variant });
  };

  if (variant === "link") {
    return (
      <div className="text-center" data-testid="booking-section">
        <button
          type="button"
          onClick={openDialog}
          className="inline-flex items-center gap-2 text-lg sm:text-xl font-display font-bold text-[var(--gold)] hover:text-[var(--brand-orange-bright)] underline underline-offset-4 decoration-[var(--gold)]/40 transition"
          data-testid="booking-link-trigger"
        >
          <Calendar size={18} />
          {title}
        </button>
        <BookingDialog open={open} onOpenChange={setOpen} title={title} subtitle={subtitle} />
      </div>
    );
  }

  return (
    <section className="section-pad bg-radial-blue relative" data-testid="booking-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--brand-blue)]/40 bg-[var(--brand-blue)]/10 mb-5">
          <Calendar size={14} className="text-[var(--brand-blue-bright)]" />
          <span className="text-xs uppercase tracking-widest text-[var(--brand-blue-bright)] font-semibold">
            {t("booking.tag")}
          </span>
        </span>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
          {title}
        </h2>
        <p className="text-white/65 max-w-2xl mx-auto text-lg mb-8">{subtitle}</p>
        <button onClick={openDialog} className="btn-gold text-base py-3 px-8" data-testid="booking-open-trigger">
          <Calendar size={16} /> {t("booking.cta")}
        </button>
      </div>
      <BookingDialog open={open} onOpenChange={setOpen} title={title} subtitle={subtitle} />
    </section>
  );
}
